import axios from 'axios';

/*
 * axios wrapper that reproduces the two vue-resource behaviours the app relies
 * on, so dropping the (retired) vue-resource dependency needs no changes at the
 * ~190 call sites:
 *
 *  1. Response shape — successful responses expose `.body` (alias of `.data`),
 *     and rejected promises resolve to a response-like object (with `.status`,
 *     `.data`, `.body`, `.headers`) rather than an axios Error, so existing
 *     `.then(onOk, response => response.status)` / `response.body.errors`
 *     handlers keep working.
 *
 *  2. `this` binding — vue-resource bound `.then()`/`.catch()` callbacks to the
 *     calling Vue instance. axios does not. A per-component `$http` (installed
 *     via a beforeCreate mixin) binds callbacks back to the component, so
 *     `this.$http.post(...).then(function (r) { this.loginUser(r.body); })`
 *     keeps working with `this` === the component.
 */
const http = axios.create();

http.interceptors.response.use(
    function (response) {
        response.body = response.data;
        return response;
    },
    function (error) {
        if (error.response) {
            error.response.body = error.response.data;
            return Promise.reject(error.response);
        }
        return Promise.reject(error);
    }
);

// Wrap an axios promise so its callbacks run with `this` === the component.
function bindToComponent(component) {
    const bind = function (fn) {
        return typeof fn === 'function' ? fn.bind(component) : fn;
    };
    const wrap = function (promise) {
        return {
            then: function (onOk, onErr) {
                return wrap(promise.then(bind(onOk), bind(onErr)));
            },
            catch: function (onErr) {
                return wrap(promise.catch(bind(onErr)));
            },
            finally: function (onFinally) {
                return wrap(promise.finally ? promise.finally(onFinally) : promise);
            }
        };
    };

    const methods = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'request'];
    const bound = {};
    methods.forEach(function (method) {
        bound[method] = function () {
            return wrap(http[method].apply(http, arguments));
        };
    });
    return bound;
}

// Vue plugin: give every component a `this.$http` bound to itself.
const HttpPlugin = {
    install: function (Vue) {
        Vue.mixin({
            beforeCreate: function () {
                this.$http = bindToComponent(this);
            }
        });
    }
};

export default HttpPlugin;
export { http };



// WEBPACK FOOTER //
// ./resources/assets/js/http.js